import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Croissant, Minus, Plus, Clock, CalendarClock, MapPin, CheckCircle2, Loader2, ShoppingBag, ArrowLeft, Mail, CreditCard, Wallet, ShieldCheck, X, Tag, Printer, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function PublicStorefront() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "1";
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selectedDay, setSelectedDay] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const dayToken = searchParams.get("day");
    const qs = [isPreview ? "preview=1" : "", dayToken ? `day=${encodeURIComponent(dayToken)}` : ""].filter(Boolean).join("&");
    api.get(`/public/bakery/${slug}${qs ? `?${qs}` : ""}`)
      .then((r) => { setData(r.data); setSelectedDay(r.data.bake_days[0] || null); })
      .catch((e) => setError(apiError(e.response?.data?.detail) || "Bakery not found"));
  }, [slug, isPreview, searchParams]);

  useEffect(() => {
    let link = document.querySelector("link[rel='canonical']");
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = `${window.location.origin}/bakery/${slug}`;
  }, [slug]);

  if (error) return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-cream px-5 text-center">
      <Croissant className="mb-4 h-10 w-10 text-terracotta" strokeWidth={1.4} />
      <h1 className="font-serif text-2xl text-cocoa">Bakery not available</h1>
      <p className="mt-2 max-w-sm text-sm text-cocoa/60">{error}</p>
      <Link to="/"><Button className="mt-6 rounded-full bg-terracotta text-warmwhite">Back to Oven &amp; Order</Button></Link>
    </div>
  );
  if (!data) return <div className="flex min-h-screen items-center justify-center bg-cream"><Loader2 className="h-8 w-8 animate-spin text-terracotta" /></div>;

  const { bakery } = data;
  const accent = bakery.accent_color || "#BD806A";

  if (confirmation) return <Confirmation confirmation={confirmation} bakery={bakery} accent={accent} onDone={() => window.location.reload()} />;

  return (
    <div className="min-h-screen bg-cream paper-grain" style={{ "--accent": accent }}>
      {data.is_preview && (
        <div className="sticky top-0 z-30 flex flex-wrap items-center justify-center gap-2 bg-cocoa px-4 py-2.5 text-center text-sm text-warmwhite" data-testid="preview-banner">
          <span>👁️ Preview mode — this is how your storefront looks{data.published ? "" : " (it isn't published yet, so customers can't see it)"}.</span>
          <Link to="/dashboard/storefront" className="font-semibold underline">Back to dashboard</Link>
        </div>
      )}
      {/* Cover */}
      <div className="relative h-56 w-full overflow-hidden sm:h-72" style={{ backgroundColor: accent }}>
        {(() => {
          const cm = bakery.cover_mode || (bakery.cover_path ? "image" : "none");
          if (cm === "color") return <div className="h-full w-full" style={{ backgroundColor: bakery.cover_color || accent }} />;
          if ((cm === "image" || cm === "curated") && bakery.cover_path) return <img src={String(bakery.cover_path).startsWith("http") ? bakery.cover_path : fileUrl(bakery.cover_path)} alt="cover" className="h-full w-full object-cover opacity-90" />;
          return null;
        })()}
        <div className="absolute inset-0 bg-cocoa/20" />
        <Link to="/" className="absolute left-5 top-5 flex items-center gap-1.5 rounded-full bg-white/20 px-3 py-1.5 text-sm text-white backdrop-blur-sm hover:bg-white/30" data-testid="back-home"><ArrowLeft className="h-4 w-4" /> Oven &amp; Order</Link>
      </div>

      <div className="mx-auto max-w-3xl px-5">
        {/* Logo overlapping the cover */}
        {(bakery.logo_mode || (bakery.logo_path ? "image" : "none")) !== "none" && (
          <div className="relative z-20 -mt-12 flex h-24 w-24 items-center justify-center overflow-hidden rounded-3xl border-4 border-cream shadow-[0_12px_40px_rgba(64,53,47,0.14)]" style={{ backgroundColor: (bakery.logo_mode === "monogram") ? (bakery.logo_bg_color || accent) : `${accent}22` }} data-testid="storefront-logo">
            {(() => {
              const lm = bakery.logo_mode || (bakery.logo_path ? "image" : "none");
              if (lm === "monogram") return <span className="font-serif text-3xl font-semibold" style={{ color: bakery.logo_text_color || "#FFF8F0" }}>{bakery.logo_monogram || (bakery.name || "B").charAt(0).toUpperCase()}</span>;
              if (bakery.logo_path) return <img src={String(bakery.logo_path).startsWith("http") ? bakery.logo_path : fileUrl(bakery.logo_path)} alt="logo" className="h-full w-full object-cover" />;
              return <Croissant className="h-10 w-10" strokeWidth={1.4} style={{ color: accent }} />;
            })()}
          </div>
        )}
        {/* Bakery header */}
        <div className="mt-4 rounded-2xl border border-cocoa/10 bg-warmwhite p-6 shadow-[0_4px_20px_rgba(64,53,47,0.05)]">
          <h1 className="font-serif text-3xl font-semibold leading-tight text-cocoa sm:text-4xl">{bakery.name}</h1>
          {bakery.location && <p className="mt-1 flex items-center gap-1 text-sm text-cocoa/60"><MapPin className="h-3.5 w-3.5" /> {bakery.location}</p>}
          {bakery.description && <p className="mt-3 text-sm leading-relaxed text-cocoa/70">{bakery.description}</p>}
          {bakery.additional_info && <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-cocoa/60" data-testid="storefront-additional-info-display">{bakery.additional_info}</p>}
          {bakery.online_payments_enabled && (
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-sage/25 px-3 py-1 text-xs font-medium text-cocoa" data-testid="online-payments-badge">
              <ShieldCheck className="h-3.5 w-3.5" style={{ color: accent }} /> Secure card payments — pay online & confirm instantly
            </span>
          )}
        </div>

        {data.bake_days.length === 0 ? (
          <div className="my-16 text-center">
            <CalendarClock className="mx-auto mb-4 h-10 w-10 text-cocoa/30" strokeWidth={1.4} />
            <h2 className="font-serif text-2xl text-cocoa">No open bake days right now</h2>
            <p className="mt-2 text-sm text-cocoa/60">Check back soon — {bakery.name} will open preorders for the next bake day here.</p>
          </div>
        ) : (
          <>
            {/* Bake day tabs */}
            {data.bake_days.length > 1 && (
              <div className="mt-6 flex flex-wrap gap-2">
                {data.bake_days.map((d) => (
                  <button key={d.id} onClick={() => setSelectedDay(d)} className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium transition-colors ${selectedDay?.id === d.id ? "text-white" : "bg-warmwhite text-cocoa/70 border border-cocoa/10"}`} style={selectedDay?.id === d.id ? { backgroundColor: accent } : {}} data-testid={`bakeday-tab-${d.id}`}>{d.title}{d.status === "draft" && <span className="rounded-full bg-cocoa/20 px-1.5 py-0.5 text-[10px] uppercase tracking-wide">Draft</span>}</button>
                ))}
              </div>
            )}
            {selectedDay && <OrderFlow day={selectedDay} bakery={bakery} accent={accent} slug={slug} onConfirmed={setConfirmation} />}
          </>
        )}
      </div>

      <footer className="mt-16 border-t border-cocoa/10 py-8 text-center">
        <p className="text-sm text-cocoa/50">Powered by <Link to="/" className="font-medium" style={{ color: accent }}>Oven &amp; Order</Link></p>
      </footer>
    </div>
  );
}

function OrderFlow({ day, bakery, accent, slug, onConfirmed }) {
  const [qty, setQty] = useState({});
  const [windowId, setWindowId] = useState("");
  const [info, setInfo] = useState({ customer_name: "", customer_email: "", customer_phone: "", notes: "" });
  const [payMethod, setPayMethod] = useState(bakery.online_payments_enabled ? "online" : "later");
  const [submitting, setSubmitting] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);
  const [promoMsg, setPromoMsg] = useState("");
  const [checkingPromo, setCheckingPromo] = useState(false);
  const [errors, setErrors] = useState({});
  const [nutriOpen, setNutriOpen] = useState({});
  const clearErr = (k) => setErrors((e) => (e[k] ? { ...e, [k]: null } : e));
  const [clientToken] = useState(() => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`));

  useEffect(() => { setQty({}); setWindowId(""); setPromo(null); setPromoInput(""); setPromoMsg(""); }, [day.id]);

  const setItemQty = (p, delta) => {
    setQty((q) => {
      const cur = q[p.id] || 0;
      const next = Math.max(0, Math.min(p.remaining, cur + delta));
      return { ...q, [p.id]: next };
    });
    clearErr("items");
  };

  const items = day.products.filter((p) => qty[p.id] > 0).map((p) => ({ ...p, quantity: qty[p.id] }));
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const round2 = (x) => Math.round(x * 100) / 100;
  const sale = day.sale;
  const saleFactor = sale && sale.discount_type === "percent" ? 1 - sale.value / 100 : null;
  const saleLabel = sale ? (sale.discount_type === "percent" ? `${sale.value}% off this bake day` : `$${Number(sale.value).toFixed(2)} off your order`) : "";
  // Manual promo takes precedence; otherwise apply the bake day's automatic sale.
  const activeDiscount = promo ? { discount_type: promo.discount_type, value: promo.value, min_subtotal: promo.min_subtotal || 0 }
    : (sale && subtotal >= (sale.min_subtotal || 0) ? { discount_type: sale.discount_type, value: sale.value } : null);
  const discount = activeDiscount ? round2(Math.min(activeDiscount.discount_type === "percent" ? subtotal * activeDiscount.value / 100 : activeDiscount.value, subtotal)) : 0;
  const total = Math.max(0, round2(subtotal - discount));
  const closeDate = day.preorder_close ? new Date(day.preorder_close) : null;

  useEffect(() => {
    if (promo && promo.min_subtotal && subtotal > 0 && subtotal < promo.min_subtotal) {
      setPromo(null);
      setPromoMsg(`Spend at least $${Number(promo.min_subtotal).toFixed(2)} to use ${promo.code}.`);
    }
  }, [subtotal]); // eslint-disable-line react-hooks/exhaustive-deps

  const applyPromo = async () => {
    if (!promoInput.trim()) return;
    setCheckingPromo(true); setPromoMsg("");
    try {
      const { data } = await api.post(`/public/bakery/${slug}/promo`, { code: promoInput.trim(), subtotal });
      if (data.valid) { setPromo(data); setPromoMsg(data.message); }
      else { setPromo(null); setPromoMsg(data.message); }
    } catch (e) { setPromo(null); setPromoMsg(apiError(e.response?.data?.detail) || "Couldn't check that code"); }
    finally { setCheckingPromo(false); }
  };
  const clearPromo = () => { setPromo(null); setPromoInput(""); setPromoMsg(""); };

  const submit = async () => {
    if (day.status === "draft") return toast.error("This is a draft bake day — publish it as ‘Open’ to accept orders.");
    const errs = {};
    if (items.length === 0) errs.items = "Add at least one item to your order.";
    if (!windowId) errs.window = "Choose a pickup time.";
    if (!info.customer_name.trim()) errs.customer_name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(info.customer_email.trim())) errs.customer_email = "Please enter a valid email address.";
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please fix the highlighted fields to place your order.");
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      const { data } = await api.post(`/public/bakery/${slug}/order`, {
        bake_day_id: day.id, pickup_window_id: windowId,
        customer_name: info.customer_name, customer_email: info.customer_email,
        customer_phone: info.customer_phone, notes: info.notes,
        promo_code: promo ? promo.code : "",
        client_token: clientToken,
        items: items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
      });
      if (payMethod === "online") {
        const { data: pay } = await api.post(`/payments/order-checkout`, { order_id: data.order_id, payment_token: data.payment_token, origin_url: window.location.origin });
        window.location.href = pay.checkout_url;
        return;
      }
      onConfirmed(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not place order");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mt-6 space-y-6 pb-32">
      {/* Bake day info */}
      <div className="rounded-2xl border border-cocoa/10 bg-warmwhite p-5">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="flex items-center gap-1.5 text-cocoa"><CalendarClock className="h-4 w-4" style={{ color: accent }} /> Pickup {new Date(day.pickup_date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}</span>
          {day.location_name && <span className="flex items-center gap-1.5 text-cocoa"><MapPin className="h-4 w-4" style={{ color: accent }} /> {day.location_name}{day.location_address ? ` · ${day.location_address}` : ""}</span>}
          {closeDate && <span className="flex items-center gap-1.5 text-cocoa/60"><Clock className="h-4 w-4" /> Orders close {closeDate.toLocaleDateString(undefined, { month: "short", day: "numeric" })} at {closeDate.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}</span>}
        </div>
        {sale && (
          <span className="mt-3 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ backgroundColor: accent }} data-testid="bakeday-sale-banner">
            <Tag className="h-3.5 w-3.5" /> Sale — {saleLabel}{sale.ends_at ? ` · ends ${new Date(sale.ends_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}
          </span>
        )}
        {day.notes && <p className="mt-3 text-sm italic text-cocoa/60">{day.notes}</p>}
      </div>

      {/* Products */}
      <div>
        <h2 className="mb-3 font-serif text-2xl text-cocoa">Choose your treats</h2>
        {errors.items && <p className="mb-2 text-sm text-destructive" data-testid="err-items">{errors.items}</p>}
        <div className="space-y-3">
          {day.products.map((p) => (
            <div key={p.id} className="rounded-2xl border border-cocoa/10 bg-warmwhite p-3" data-testid={`product-${p.id}`}>
              <div className="flex items-center gap-4">
              <div className="h-20 w-20 flex-shrink-0 overflow-hidden rounded-xl bg-cream">
                {p.photo_path ? <img src={fileUrl(p.photo_path)} alt={p.name} className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center text-cocoa/20"><ShoppingBag className="h-7 w-7" strokeWidth={1.2} /></div>}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2"><p className="font-serif text-lg text-cocoa">{p.name}</p>{p.sold_out && <span className="rounded-full bg-cocoa/10 px-2 py-0.5 text-xs font-medium text-cocoa/60">Sold out</span>}</div>
                {p.description && <p className="line-clamp-2 text-sm text-cocoa/60">{p.description}</p>}
                {p.dietary_tags?.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {p.dietary_tags.map((t) => <span key={t} className="rounded-full bg-sage/25 px-2 py-0.5 text-[10px] font-medium text-cocoa">{t}</span>)}
                  </div>
                )}
                {p.allergens?.length > 0 && (
                  <div className="mt-1.5 flex items-start gap-1.5 rounded-lg border border-amber-300 bg-amber-50 px-2.5 py-1.5" data-testid={`allergens-${p.id}`}>
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-600" strokeWidth={2.2} />
                    <p className="text-xs leading-snug text-amber-900"><span className="font-bold uppercase tracking-wide">Contains:</span> {p.allergens.join(", ")}</p>
                  </div>
                )}
                {saleFactor != null ? (
                  <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-sm" data-testid={`product-sale-${p.id}`}>
                    <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white" style={{ backgroundColor: accent }}>Sale</span>
                    <span className="text-cocoa/40 line-through">${p.price.toFixed(2)}</span>
                    <span className="font-semibold" style={{ color: accent }}>${(p.price * saleFactor).toFixed(2)}</span>
                    {!p.sold_out && p.remaining <= 5 && <span className="text-xs font-normal text-cocoa/50">Only {p.remaining} left</span>}
                  </p>
                ) : (
                  <p className="mt-0.5 text-sm font-medium" style={{ color: accent }}>${p.price.toFixed(2)}{!p.sold_out && p.remaining <= 5 && <span className="ml-2 text-xs font-normal text-cocoa/50">Only {p.remaining} left</span>}</p>
                )}
              </div>
              {p.sold_out ? (
                <span className="text-sm text-cocoa/40">—</span>
              ) : (
                <div className="flex items-center gap-2">
                  <button onClick={() => setItemQty(p, -1)} disabled={!qty[p.id]} aria-label={`Remove one ${p.name}`} className="flex h-8 w-8 items-center justify-center rounded-full border border-cocoa/15 text-cocoa disabled:opacity-30" data-testid={`minus-${p.id}`}><Minus className="h-4 w-4" /></button>
                  <span className="w-5 text-center text-sm font-medium text-cocoa" aria-live="polite" aria-label={`${qty[p.id] || 0} ${p.name} in cart`} data-testid={`qty-${p.id}`}>{qty[p.id] || 0}</span>
                  <button onClick={() => setItemQty(p, 1)} disabled={(qty[p.id] || 0) >= p.remaining} aria-label={`Add one ${p.name}`} className="flex h-8 w-8 items-center justify-center rounded-full text-white disabled:opacity-30" style={{ backgroundColor: accent }} data-testid={`plus-${p.id}`}><Plus className="h-4 w-4" /></button>
                </div>
              )}
              </div>
              {hasNutrition(p.nutrition) && (
                <div className="mt-2">
                  <button type="button" onClick={() => setNutriOpen((o) => ({ ...o, [p.id]: !o[p.id] }))} aria-expanded={!!nutriOpen[p.id]} className="text-xs font-medium underline underline-offset-2" style={{ color: accent }} data-testid={`nutri-toggle-${p.id}`}>{nutriOpen[p.id] ? "Hide nutrition facts" : "Nutrition facts"}</button>
                  {nutriOpen[p.id] && <NutritionPanel n={p.nutrition} />}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Pickup windows */}
      <div>
        <h2 className="mb-3 font-serif text-2xl text-cocoa">Pick a pickup time</h2>
        {errors.window && <p className="mb-2 text-sm text-destructive" data-testid="err-window">{errors.window}</p>}
        <div className="grid gap-2 sm:grid-cols-3">
          {day.pickup_windows.map((w) => (
            <button key={w.id} disabled={w.full} onClick={() => { setWindowId(w.id); clearErr("window"); }} className={`rounded-2xl border p-3 text-center text-sm transition-colors disabled:opacity-40 ${windowId === w.id ? "text-white" : "border-cocoa/10 bg-warmwhite text-cocoa"}`} style={windowId === w.id ? { backgroundColor: accent, borderColor: accent } : {}} data-testid={`window-${w.id}`}>
              <span className="font-medium">{w.label || `${w.start} – ${w.end}`}</span>
              {w.full && <span className="mt-1 block text-xs">Full</span>}
              {!w.full && w.remaining_slots != null && w.remaining_slots <= 3 && <span className="mt-1 block text-xs opacity-70">{w.remaining_slots} slots left</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Customer info */}
      <div>
        <h2 className="mb-3 font-serif text-2xl text-cocoa">Your details</h2>
        <div className="space-y-3 rounded-2xl border border-cocoa/10 bg-warmwhite p-5">
          <div><Label className="text-cocoa">Name *</Label><Input value={info.customer_name} onChange={(e) => { setInfo({ ...info, customer_name: e.target.value }); clearErr("customer_name"); }} className={`mt-1.5 rounded-xl bg-cream ${errors.customer_name ? "border-destructive" : ""}`} data-testid="checkout-name" />{errors.customer_name && <p className="mt-1 text-xs text-destructive" data-testid="err-name">{errors.customer_name}</p>}</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-cocoa">Email *</Label><Input type="email" name="email" autoComplete="email" value={info.customer_email} onChange={(e) => { setInfo((prev) => ({ ...prev, customer_email: e.target.value })); clearErr("customer_email"); }} className={`mt-1.5 rounded-xl bg-cream ${errors.customer_email ? "border-destructive" : ""}`} data-testid="checkout-email" />{errors.customer_email && <p className="mt-1 text-xs text-destructive" data-testid="err-email">{errors.customer_email}</p>}</div>
            <div><Label className="text-cocoa">Phone</Label><Input value={info.customer_phone} onChange={(e) => setInfo({ ...info, customer_phone: e.target.value })} className="mt-1.5 rounded-xl bg-cream" data-testid="checkout-phone" /></div>
          </div>
          <div><Label className="text-cocoa">Order note</Label><Textarea value={info.notes} onChange={(e) => setInfo({ ...info, notes: e.target.value })} placeholder="Any special requests?" className="mt-1.5 rounded-xl bg-cream" rows={2} data-testid="checkout-notes" /></div>
        </div>
      </div>

      {/* Payment method */}
      <div>
        <h2 className="mb-3 font-serif text-2xl text-cocoa">How would you like to pay?</h2>
        {bakery.online_payments_enabled ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <button onClick={() => setPayMethod("online")} className={`rounded-2xl border p-4 text-left transition-colors ${payMethod === "online" ? "text-white" : "border-cocoa/10 bg-warmwhite text-cocoa"}`} style={payMethod === "online" ? { backgroundColor: accent, borderColor: accent } : {}} data-testid="pay-online-option">
              <span className="flex items-center gap-2 font-medium"><CreditCard className="h-4 w-4" /> Pay online now</span>
              <span className={`mt-1 block text-sm ${payMethod === "online" ? "text-white/80" : "text-cocoa/55"}`}>Secure card payment — your order is confirmed instantly.</span>
            </button>
            <button onClick={() => setPayMethod("later")} className={`rounded-2xl border p-4 text-left transition-colors ${payMethod === "later" ? "text-white" : "border-cocoa/10 bg-warmwhite text-cocoa"}`} style={payMethod === "later" ? { backgroundColor: accent, borderColor: accent } : {}} data-testid="pay-later-option">
              <span className="flex items-center gap-2 font-medium"><Wallet className="h-4 w-4" /> Pay at pickup</span>
              <span className={`mt-1 block text-sm ${payMethod === "later" ? "text-white/80" : "text-cocoa/55"}`}>{bakery.payment_instructions || "Follow the bakery's payment instructions."}</span>
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-cocoa/10 bg-warmwhite p-4" data-testid="manual-payment-only">
            <span className="flex items-center gap-2 font-medium text-cocoa"><Wallet className="h-4 w-4" /> Pay at pickup</span>
            <p className="mt-1 text-sm text-cocoa/60">{bakery.payment_instructions || "Follow the bakery's payment instructions after placing your order."}</p>
          </div>
        )}
      </div>

      {/* Promo code */}
      <div className="rounded-2xl border border-cocoa/10 bg-warmwhite p-4" data-testid="promo-section">
        <Label className="text-cocoa">Have a promo code?</Label>
        {promo ? (
          <div className="mt-1.5 flex items-center justify-between rounded-xl bg-sage/20 px-3 py-2 text-sm">
            <span className="font-medium text-cocoa" data-testid="promo-applied">Code {promo.code} applied — you save ${discount.toFixed(2)}</span>
            <button onClick={clearPromo} className="text-cocoa/50 hover:text-cocoa" data-testid="promo-remove"><X className="h-4 w-4" /></button>
          </div>
        ) : (
          <div className="mt-1.5 flex gap-2">
            <Input value={promoInput} onChange={(e) => setPromoInput(e.target.value.toUpperCase())} placeholder="Enter code" className="rounded-xl bg-cream uppercase" data-testid="promo-input" />
            <Button onClick={applyPromo} disabled={checkingPromo || !promoInput.trim()} variant="outline" className="rounded-full border-cocoa/20 text-cocoa" data-testid="promo-apply">{checkingPromo ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply"}</Button>
          </div>
        )}
        {promoMsg && !promo && <p className="mt-1.5 text-xs text-destructive" data-testid="promo-msg">{promoMsg}</p>}
      </div>

      {/* Sticky summary — only pins to the bottom once there are items, so it never floats over the empty form */}
      <div className={`${items.length > 0 ? "sticky bottom-4" : ""} rounded-2xl border border-cocoa/10 bg-warmwhite p-4 shadow-[0_-4px_30px_rgba(64,53,47,0.1)]`}>
        <div className="mb-3 space-y-1">
          {discount > 0 && (
            <>
              <div className="flex items-center justify-between text-sm text-cocoa/60"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              <div className="flex items-center justify-between text-sm text-green-700" data-testid="checkout-discount"><span>{promo ? `Discount (${promo.code})` : "Sale"}</span><span>-${discount.toFixed(2)}</span></div>
            </>
          )}
          <div className="flex items-center justify-between">
            <span className="text-sm text-cocoa/60">{items.reduce((s, i) => s + i.quantity, 0)} item{items.reduce((s, i) => s + i.quantity, 0) !== 1 ? "s" : ""}</span>
            <span className="font-serif text-2xl text-cocoa" data-testid="checkout-total">${total.toFixed(2)}</span>
          </div>
        </div>
        <Button onClick={submit} disabled={submitting} className="w-full rounded-full py-6 text-base text-white disabled:opacity-50" style={{ backgroundColor: accent }} data-testid="place-order-btn">
          {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : items.length === 0 ? "Add items to your order" : (bakery.online_payments_enabled && payMethod === "online" ? `Pay $${total.toFixed(2)} & place order` : "Place preorder")}
        </Button>
      </div>
    </div>
  );
}

function Confirmation({ confirmation, bakery, accent, onDone }) {
  const c = confirmation;
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);
  const [cooling, setCooling] = useState(false);
  const resend = async () => {
    setResending(true);
    try {
      const { data } = await api.post(`/public/order/${c.order_id}/resend`, { payment_token: c.payment_token });
      if (data.sent) { setResent(true); toast.success(`Confirmation re-sent to ${data.email}`); }
      else toast.error("We couldn't resend the email just now.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't resend the email"); }
    finally { setResending(false); setCooling(true); setTimeout(() => setCooling(false), 8000); }
  };
  return (
    <div className="min-h-screen bg-cream paper-grain">
      <div className="mx-auto max-w-lg px-5 py-16 text-center">
        <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full" style={{ backgroundColor: `${accent}22` }}>
          <CheckCircle2 className="h-9 w-9" style={{ color: accent }} strokeWidth={1.5} />
        </div>
        <h1 className="font-serif text-3xl font-semibold text-cocoa">Order confirmed!</h1>
        <p className="mt-2 text-sm text-cocoa/60">Thank you for ordering from {c.bakery_name}.</p>

        <div className="mt-8 rounded-2xl border border-cocoa/10 bg-warmwhite p-6 text-left">
          <div className="flex items-center justify-between border-b border-cocoa/10 pb-4">
            <span className="text-sm text-cocoa/60">Order number</span>
            <span className="font-serif text-xl text-cocoa" data-testid="confirmation-number">#{c.order_number}</span>
          </div>
          <div className="space-y-2 py-4">
            {c.items.map((i, idx) => (
              <div key={idx} className="flex justify-between text-sm"><span className="text-cocoa/70">{i.name} × {i.quantity}</span><span className="text-cocoa">${(i.price * i.quantity).toFixed(2)}</span></div>
            ))}
            {c.discount > 0 && (
              <>
                <div className="flex justify-between border-t border-cocoa/10 pt-2 text-sm text-cocoa/60"><span>Subtotal</span><span>${c.subtotal.toFixed(2)}</span></div>
                <div className="flex justify-between text-sm text-green-700"><span>{c.promo_code && !c.promo_code.startsWith("SALE-") ? `Discount (${c.promo_code})` : "Sale"}</span><span>-${c.discount.toFixed(2)}</span></div>
              </>
            )}
            <div className="flex justify-between border-t border-cocoa/10 pt-2 font-semibold text-cocoa"><span>Total</span><span>${(c.total ?? c.subtotal).toFixed(2)}</span></div>
          </div>
          <div className="space-y-3 border-t border-cocoa/10 pt-4 text-sm">
            <Info label="Pickup date" value={new Date(c.pickup_date + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })} />
            <Info label="Pickup window" value={c.pickup_window} />
            {c.pickup_location && <Info label="Pickup location" value={c.pickup_location + (c.pickup_address ? ` · ${c.pickup_address}` : "")} />}
            {c.pickup_instructions && <Info label="Pickup instructions" value={c.pickup_instructions} />}
            {c.payment_instructions && <Info label="Payment" value={c.payment_instructions} />}
            {c.contact_email && <Info label="Questions?" value={c.contact_email} />}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-center gap-2 text-xs text-cocoa/50">
          <Mail className="h-3.5 w-3.5" />
          {c.email_configured ? (c.email_status?.customer ? "A confirmation email is on its way." : "We saved your order — email delivery is being finalized.") : "Email delivery isn't configured yet, so please save your order number."}
        </div>

        <div className="mt-8 flex flex-col items-center gap-3 print:hidden">
          <Button onClick={onDone} className="rounded-full px-8 py-6 text-white" style={{ backgroundColor: accent }} data-testid="confirmation-done">Place another order</Button>
          <div className="flex flex-wrap justify-center gap-2">
            <Button variant="outline" onClick={() => window.print()} className="rounded-full border-cocoa/20 text-cocoa" data-testid="confirmation-print"><Printer className="mr-1.5 h-4 w-4" /> Print receipt</Button>
            {c.order_id && c.email_configured && (
              <Button variant="outline" onClick={resend} disabled={resending || resent || cooling} className="rounded-full border-cocoa/20 text-cocoa" data-testid="confirmation-resend">{resending ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Mail className="mr-1.5 h-4 w-4" />}{resent ? "Email sent" : "Resend email"}</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function hasNutrition(n) {
  return !!(n && (n.serving_size || n.calories != null || n.fat_g != null || n.saturated_fat_g != null || n.carbs_g != null || n.fiber_g != null || n.sugar_g != null || n.protein_g != null || n.sodium_mg != null));
}

function NutritionPanel({ n }) {
  const rows = [
    ["Calories", n.calories != null ? String(n.calories) : null],
    ["Total Fat", n.fat_g != null ? `${n.fat_g} g` : null],
    ["Saturated Fat", n.saturated_fat_g != null ? `${n.saturated_fat_g} g` : null],
    ["Sodium", n.sodium_mg != null ? `${n.sodium_mg} mg` : null],
    ["Total Carbohydrate", n.carbs_g != null ? `${n.carbs_g} g` : null],
    ["Dietary Fiber", n.fiber_g != null ? `${n.fiber_g} g` : null],
    ["Sugars", n.sugar_g != null ? `${n.sugar_g} g` : null],
    ["Protein", n.protein_g != null ? `${n.protein_g} g` : null],
  ].filter((r) => r[1] != null && r[1] !== "");
  return (
    <div className="mt-1.5 rounded-xl border border-cocoa/10 bg-cream/70 p-2.5 text-xs" data-testid="nutrition-panel">
      {n.serving_size && <p className="mb-1 font-medium text-cocoa">Serving size: {n.serving_size}</p>}
      <div className="divide-y divide-cocoa/10">
        {rows.map(([l, v]) => <div key={l} className="flex justify-between py-0.5"><span className="text-cocoa/70">{l}</span><span className="text-cocoa">{v}</span></div>)}
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-cocoa/40">{label}</p>
      <p className="mt-0.5 text-cocoa/80">{value}</p>
    </div>
  );
}
